/**
 * mandate-registration-view.ts - the area «Mandate & Eintragungen» of a customer (Stefan P5 V2.0 Kap.
 * 7.7, 10.5, Use Case 14): one row per Versicherungsbeziehung (the customer and one insurer), the
 * Analysemandat and the Brokermandat side by side, the policies with their derived registration status,
 * the proofs of the Meldung, the header tile «Eintragung bei Versicherern» and the mirror on the
 * Versicherer.
 *
 * The Versicherungsbeziehung is derived, never stored: the api's query returns one raw row per insurer
 * key (the normalised UID, or the name of a declared insurance without UID) and `buildCarrierRows`
 * assembles it. The status words come from `registrationStatusLabel` and `policyRegistrationLabel`, the
 * only places they live.
 *
 * Pure data and pure functions only, shared by api and web.
 */

import type { MandateInsurerNotificationStatus } from './versicherer-benachrichtigung';
import { isNotificationOverdue } from './versicherer-benachrichtigung';
import { INSURANCE_RELATIONSHIP_SOURCE_LABEL } from './versicherer-benachrichtigung';
import type { InsuranceRelationshipSource } from './insurance-relationship';
import type { PolicyStatus } from './policy';
import { registrationStatusLabel } from './mandate-registration';
import type { RegistrationExceptionCode, RegistrationReportMode } from './mandate-registration';
import { derivePolicyRegistrationStatus, policyRegistrationLabel } from './mandate-registration-reply';
import type { PolicyRegistrationStatus } from './mandate-registration-reply';
import type { CustomerStatusValue } from './customer';
import type { TerminationSummaryDto } from './mandate-termination';

// ── filters, sorting, limits ─────────────────────────────────────────────────────────────────

/** A broker row in one of these statuses (or a policy exception on the row) needs a broker. */
export const HANDLUNGSBEDARF_STATUSES: readonly MandateInsurerNotificationStatus[] = [
  'zur_freigabe',
  'zurueckgestellt',
  'klaerung',
  'rueckfrage',
  'abgelehnt',
];

/** The status filter of the area: the words of the tile, each a set of row statuses. */
export const REGISTRATION_VIEW_STATUS_FILTER: Record<string, readonly MandateInsurerNotificationStatus[]> = {
  gemeldet: ['sending', 'benachrichtigt'],
  erinnert: ['erinnert'],
  klaerung: ['klaerung'],
  rueckfrage: ['rueckfrage'],
  eingetragen: ['bestaetigt'],
  abgelehnt: ['abgelehnt'],
  geschlossen: ['manuell_geschlossen'],
  zur_freigabe: ['zur_freigabe'],
  zurueckgestellt: ['zurueckgestellt'],
};
export const REGISTRATION_VIEW_STATUS_FILTER_VALUES = [
  'gemeldet',
  'erinnert',
  'klaerung',
  'rueckfrage',
  'eingetragen',
  'abgelehnt',
  'geschlossen',
  'zur_freigabe',
  'zurueckgestellt',
] as const;
export type RegistrationViewStatusFilter = (typeof REGISTRATION_VIEW_STATUS_FILTER_VALUES)[number];

/** The filter's words: the row words of `registrationStatusLabel`. */
export const REGISTRATION_VIEW_STATUS_FILTER_LABEL: Record<RegistrationViewStatusFilter, string> = {
  gemeldet: registrationStatusLabel({ status: 'benachrichtigt' }),
  erinnert: registrationStatusLabel({ status: 'erinnert' }),
  klaerung: registrationStatusLabel({ status: 'klaerung' }),
  rueckfrage: registrationStatusLabel({ status: 'rueckfrage' }),
  eingetragen: registrationStatusLabel({ status: 'bestaetigt' }),
  abgelehnt: registrationStatusLabel({ status: 'abgelehnt' }),
  geschlossen: registrationStatusLabel({ status: 'manuell_geschlossen' }),
  zur_freigabe: registrationStatusLabel({ status: 'zur_freigabe' }),
  zurueckgestellt: registrationStatusLabel({ status: 'zurueckgestellt' }),
};

export const REGISTRATION_VIEW_MANDATE_KIND_VALUES = ['analyse', 'broker'] as const;
export type RegistrationViewMandateKind = (typeof REGISTRATION_VIEW_MANDATE_KIND_VALUES)[number];

export const REGISTRATION_VIEW_SORT_VALUES = ['carrier', 'status'] as const;
export type RegistrationViewSort = (typeof REGISTRATION_VIEW_SORT_VALUES)[number];

/**
 * The key of the group of the customer's policies without insurer UID («Ohne Versicherer-UID»): no
 * insurer, no Meldung; the broker sets the UID on the policy. Listed last.
 */
export const REGISTRATION_NO_CARRIER_UID_KEY = 'ohne_uid';

/** The longest carrier key a dossier export accepts (a normalised UID is 12, a name key up to 60). */
export const REGISTRATION_CARRIER_KEY_MAX = 60;

export interface RegistrationViewFilters {
  status?: RegistrationViewStatusFilter;
  mandate_kind?: RegistrationViewMandateKind;
  handlungsbedarf?: boolean;
  sort?: RegistrationViewSort;
}

// ── the tile «Eintragung bei Versicherern» ──────────────────────────────────────────────────

export interface RegistrationTileCounts {
  /** Insurers with a Brokermandat row. */
  total: number;
  eingetragen: number;
  gemeldet: number;
  erinnert: number;
  klaerung: number;
  /** Gemeldet (automatisch übertragen): a Meldung to a new insurer of a registered customer. */
  auto_uebertragen: number;
  abgelehnt: number;
  zur_freigabe: number;
  zurueckgestellt: number;
  rueckfrage: number;
  geschlossen: number;
  /** The insurer de-registered the broker (a mandate termination); absent on older payloads. */
  ausgetragen?: number;
}

export interface RegistrationTilesDto extends RegistrationTileCounts {
  label: string;
}

const EMPTY_COUNTS: RegistrationTileCounts = {
  total: 0,
  eingetragen: 0,
  gemeldet: 0,
  erinnert: 0,
  klaerung: 0,
  auto_uebertragen: 0,
  abgelehnt: 0,
  zur_freigabe: 0,
  zurueckgestellt: 0,
  rueckfrage: 0,
  geschlossen: 0,
  ausgetragen: 0,
};

/** The chips after «x von n eingetragen», in this order, with their words. */
const TILE_CHIPS: ReadonlyArray<[keyof RegistrationTileCounts, string]> = [
  ['gemeldet', 'gemeldet'],
  ['auto_uebertragen', 'automatisch übertragen'],
  ['erinnert', 'erinnert'],
  ['klaerung', 'Klärung'],
  ['rueckfrage', 'Rückfrage'],
  ['zur_freigabe', 'zur Freigabe'],
  ['zurueckgestellt', 'zurückgestellt'],
  ['abgelehnt', 'abgelehnt'],
  ['ausgetragen', 'ausgetragen'],
];

/** «3 von 4 eingetragen · 1 Klärung»; «Keine Meldungen» without any Brokermandat row. Pure. */
export function registrationTileLabel(tiles: Partial<RegistrationTileCounts> & { total: number }): string {
  const t = { ...EMPTY_COUNTS, ...tiles };
  if (t.total <= 0) return 'Keine Meldungen';
  const chips = TILE_CHIPS.filter(([key]) => (t[key] ?? 0) > 0).map(([key, word]) => `${t[key]} ${word}`);
  return [`${t.eingetragen} von ${t.total} eingetragen`, ...chips].join(' · ');
}

/** The counts over the newest Brokermandat row of each insurer, and the label. Pure. */
export function computeRegistrationTiles(
  rows: ReadonlyArray<{ status: MandateInsurerNotificationStatus; report_mode?: string | null }>,
): RegistrationTilesDto {
  const t: RegistrationTileCounts = { ...EMPTY_COUNTS };
  for (const r of rows) {
    t.total += 1;
    switch (r.status) {
      case 'bestaetigt':
        t.eingetragen += 1;
        break;
      case 'sending':
      case 'benachrichtigt':
        if (r.report_mode === 'automatisch_uebertragen') t.auto_uebertragen += 1;
        else t.gemeldet += 1;
        break;
      case 'erinnert':
        t.erinnert += 1;
        break;
      case 'klaerung':
        t.klaerung += 1;
        break;
      case 'rueckfrage':
        t.rueckfrage += 1;
        break;
      case 'abgelehnt':
        t.abgelehnt += 1;
        break;
      case 'zur_freigabe':
        t.zur_freigabe += 1;
        break;
      case 'zurueckgestellt':
        t.zurueckgestellt += 1;
        break;
      case 'manuell_geschlossen':
        t.geschlossen += 1;
        break;
      case 'ausgetragen':
      case 'informiert':
        t.ausgetragen = (t.ausgetragen ?? 0) + 1;
        break;
      default: {
        const unhandled: never = r.status;
        throw new Error(`unhandled registration status ${String(unhandled)}`);
      }
    }
  }
  return { ...t, label: registrationTileLabel(t) };
}

/** The tile «Austragung bei Versicherern»: the Auflösungsmeldungen of a termination by state. */
export interface AustragungTileDto {
  gemeldet: number;
  erinnert: number;
  klaerung: number;
  ausgetragen: number;
  informiert: number;
}

/**
 * The counts over the Auflösungsmeldungen (prozess p6_aufloesung) of a customer. A planned or sent row
 * counts as gemeldet, Klärung and Rückfrage as klaerung, a confirmed row as ausgetragen; a closed row
 * (manuell_geschlossen) is not counted. Pure.
 */
export function computeAustragungTile(rows: ReadonlyArray<{ status: MandateInsurerNotificationStatus }>): AustragungTileDto {
  const t: AustragungTileDto = { gemeldet: 0, erinnert: 0, klaerung: 0, ausgetragen: 0, informiert: 0 };
  for (const r of rows) {
    switch (r.status) {
      case 'sending':
      case 'zurueckgestellt':
      case 'zur_freigabe':
      case 'benachrichtigt':
        t.gemeldet += 1;
        break;
      case 'erinnert':
        t.erinnert += 1;
        break;
      case 'klaerung':
      case 'rueckfrage':
        t.klaerung += 1;
        break;
      case 'bestaetigt':
      case 'ausgetragen':
        t.ausgetragen += 1;
        break;
      case 'informiert':
        t.informiert += 1;
        break;
      case 'abgelehnt':
      case 'manuell_geschlossen':
        break;
      default: {
        const unhandled: never = r.status;
        throw new Error(`unhandled registration status ${String(unhandled)}`);
      }
    }
  }
  return t;
}

// ── the view DTO ─────────────────────────────────────────────────────────────────────────────

export interface RegistrationViewMandateDto {
  mandate_id: string;
  process_number: string | null;
  status: string;
  signed_at: string | null;
}

export interface RegistrationViewBrokerMandateDto extends RegistrationViewMandateDto {
  process_state: 'offen' | 'gueltig' | 'abgeschlossen';
  process_closed_at: string | null;
}

export interface CarrierRowAnalyse {
  registration_id: string;
  status: MandateInsurerNotificationStatus;
  status_label: string;
  sent_at: string | null;
  confirmed_at: string | null;
}

export interface CarrierRowBroker {
  registration_id: string;
  mandate_id: string;
  status: MandateInsurerNotificationStatus;
  status_label: string;
  report_mode: RegistrationReportMode | null;
  exception_codes: RegistrationExceptionCode[];
  sent_at: string | null;
  reminded_at: string | null;
  /** The Frist of the row (Klärung after it). */
  response_due_at: string | null;
  escalated_at: string | null;
  effective_date: string | null;
  confirmed_at: string | null;
  confirmation_mode: string | null;
  rejection_reason_code: string | null;
  reminder_error_code: string | null;
  overdue: boolean;
}

export interface CarrierRowPolicy {
  policy_id: string;
  policy_number: string | null;
  line_of_business: string | null;
  policy_status: PolicyStatus;
  registration_status: PolicyRegistrationStatus;
  registration_label: string;
  inherited: boolean;
  inherited_from_policy_number: string | null;
  /** The policy is in the Policenliste the Meldung mailed; false = «nachträglich zugeordnet». */
  in_mailed_list: boolean;
  exception_reason: string | null;
  courtage_eligible_from: string | null;
  courtage_eligible_to: string | null;
}

export interface CarrierRowDeclared {
  relationship_id: string;
  sparte_text: string | null;
  policy_number: string | null;
  status: string;
  source_label: string;
}

export interface CarrierRowProofs {
  meldung: {
    communication_id: string | null;
    sent_at: string | null;
    /** The address the Meldung went to, masked (p***@axa.ch); the dossier prints it in full. */
    recipient_email_masked: string | null;
    attachment_checksum: string | null;
    document_id: string | null;
  };
  /** The outbound mail accepted by the mail server: the only proof held (no bounce sync). */
  zustellnachweis: { communication_id: string; status: 'sent' } | null;
  erinnerung: { communication_id: string | null; at: string } | null;
  bestaetigung: { communication_id: string | null; document_id: string | null; at: string | null; mode: string | null } | null;
  replies: Array<{ id: string; outcome: string; confidence: number; routing: string; decision: string | null; at: string }>;
}

export interface CarrierRow {
  /** The normalised UID, or `name:<name>` for a declared insurance without UID. */
  carrier_key: string;
  carrier_uid: string | null;
  carrier_name: string | null;
  versicherer_id: string | null;
  /** The group «Ohne Versicherer-UID» (REGISTRATION_NO_CARRIER_UID_KEY). */
  carrier_uid_missing: boolean;
  active_policy_count: number;
  handlungsbedarf: boolean;
  analyse: CarrierRowAnalyse | null;
  broker: CarrierRowBroker | null;
  policies: CarrierRowPolicy[];
  declared_only: CarrierRowDeclared[];
  /** The proofs of the Brokermandat row. */
  proofs: CarrierRowProofs | null;
  /** The newest Auflösungsmeldung of the customer at this insurer (a mandate termination), shown under the rows it de-registers. */
  aufloesung?: CarrierRowAufloesung | null;
}

/** An Auflösungsmeldung (prozess p6_aufloesung) as the area shows it. */
export interface CarrierRowAufloesung {
  registration_id: string;
  mandate_id: string;
  termination_id: string;
  status: MandateInsurerNotificationStatus;
  status_label: string;
  info_only: boolean;
  sent_at: string | null;
  response_due_at: string | null;
  deregistered_at: string | null;
  effective_date: string | null;
  escalation_task_id: string | null;
  /** the Rücknahmemeldung after a Widerruf went out (absent on older payloads). */
  withdrawal_sent_at?: string | null;
  /** the Widerruf owes this insurer a Rücknahmemeldung: an open task «Rücknahmemeldung manuell senden» names the row. */
  withdrawal_pending?: boolean;
}

/** GET /customers/:subjectType/:subjectId/mandate-registrations */
export interface MandateRegistrationsViewDto {
  subject: { subject_type: 'person' | 'firma'; subject_id: string; customer_number: string | null; status: CustomerStatusValue | string };
  mandates: { analyse: RegistrationViewMandateDto | null; broker: RegistrationViewBrokerMandateDto | null };
  /** The customer's Mandatsauflösung (P6): the live one, else the last completed one; null without one. */
  termination?: TerminationSummaryDto | null;
  tiles: RegistrationTilesDto;
  rows: CarrierRow[];
  /** The tile «Austragung bei Versicherern» while the customer has Auflösungsmeldungen; else absent. */
  austragung?: AustragungTileDto | null;
}

/** POST /customers/:subjectType/:subjectId/mandate-registrations/dossier */
export interface ExportRegistrationDossierBody {
  carrier_key?: string;
}
export interface RegistrationDossierExportDto {
  document_id: string;
  filename: string;
  download_url: string;
  scope: 'carrier' | 'customer';
  registration_ids: string[];
}

/** GET /carriers/:carrierUid/mandate-registrations: the customers registered at one insurer. */
export interface CarrierRegistrationsDto {
  items: Array<{
    subject_type: 'person' | 'firma';
    subject_id: string;
    customer_number: string | null;
    display_name: string | null;
    analyse: CarrierRowAnalyse | null;
    broker: CarrierRowBroker | null;
    active_policy_count: number;
  }>;
}

/** The activity event of the export (Kap. 12). */
export const REGISTRATION_DOSSIER_EVENTS = { EXPORTED: 'registration_dossier.exported' } as const;

// ── assembly of the query rows ───────────────────────────────────────────────────────────────

/** The policy statuses that count as active (the count in the row). */
export const ACTIVE_POLICY_STATUSES: readonly string[] = ['active', 'in_mutation', 'renewal_pending'];

/** One registration row as the query returns it (snake_case json). */
export interface RegistrationViewRegRaw {
  id: string;
  mandate_id: string;
  status: MandateInsurerNotificationStatus;
  report_mode: string | null;
  exception_codes: string[] | null;
  sent_at: string | null;
  reminded_at: string | null;
  response_due_at: string | null;
  escalated_at: string | null;
  effective_date: string | null;
  confirmed_at: string | null;
  confirmation_mode: string | null;
  rejection_reason_code: string | null;
  reminder_error_code: string | null;
  communication_id: string | null;
  reminder_communication_id: string | null;
  confirmation_communication_id: string | null;
  confirmation_document_id: string | null;
  document_id: string | null;
  attachment_checksum: string | null;
  recipient_email: string | null;
  replies?: Array<{ id: string; outcome: string; confidence: number | string; routing: string; decision: string | null; created_at: string }> | null;
}

export interface RegistrationViewPolicyRaw {
  policy_id: string;
  policy_number: string | null;
  line_of_business: string | null;
  policy_status: PolicyStatus;
  registration_id: string | null;
  registration_inherited_from_policy_id: string | null;
  inherited_from_policy_number: string | null;
  exception_reason: string | null;
  courtage_eligible_from: string | null;
  courtage_eligible_to: string | null;
  registration_status: MandateInsurerNotificationStatus | null;
  in_mailed_list: boolean | null;
}

export interface RegistrationViewDeclaredRaw {
  relationship_id: string;
  sparte_text: string | null;
  policy_number: string | null;
  status: string;
  source: string;
}

/** One row of the query: one insurer of the customer. */
export interface RegistrationViewRaw {
  carrier_key: string;
  carrier_uid: string | null;
  carrier_name: string | null;
  versicherer_id: string | null;
  analyse: RegistrationViewRegRaw | null;
  broker: RegistrationViewRegRaw | null;
  policies: RegistrationViewPolicyRaw[] | null;
  declared_only: RegistrationViewDeclaredRaw[] | null;
  /** The newest Auflösungsmeldung at this insurer, raw. */
  aufloesung?: {
    id: string;
    mandate_id: string;
    termination_id: string;
    status: MandateInsurerNotificationStatus;
    info_only: boolean;
    sent_at: string | null;
    response_due_at: string | null;
    deregistered_at: string | null;
    effective_date: string | null;
    confirmation_mode: string | null;
    escalation_task_id: string | null;
    withdrawal_sent_at?: string | null;
    withdrawal_pending?: boolean;
  } | null;
}

/** «p***@axa.ch»: the first letter and the domain. Null stays null. Pure. */
export function maskEmail(email: string | null | undefined): string | null {
  if (email === null || email === undefined || email.trim() === '') return null;
  const at = email.lastIndexOf('@');
  if (at <= 0) return '***';
  return `${email.slice(0, 1)}***${email.slice(at)}`;
}

function brokerOf(r: RegistrationViewRegRaw, nowIso: string): CarrierRowBroker {
  return {
    registration_id: r.id,
    mandate_id: r.mandate_id,
    status: r.status,
    status_label: registrationStatusLabel(r),
    report_mode: (r.report_mode as RegistrationReportMode | null) ?? null,
    exception_codes: (r.exception_codes ?? []) as RegistrationExceptionCode[],
    sent_at: r.sent_at,
    reminded_at: r.reminded_at,
    response_due_at: r.response_due_at,
    escalated_at: r.escalated_at,
    effective_date: r.effective_date,
    confirmed_at: r.confirmed_at,
    confirmation_mode: r.confirmation_mode,
    rejection_reason_code: r.rejection_reason_code,
    reminder_error_code: r.reminder_error_code,
    overdue: isNotificationOverdue({ status: r.status, response_due_at: r.response_due_at }, nowIso),
  };
}

function analyseOf(r: RegistrationViewRegRaw): CarrierRowAnalyse {
  return {
    registration_id: r.id,
    status: r.status,
    // the analyse slot holds p4 rows only (the read model's filter): an informed one reads «Aufgelöst (informiert)»
    status_label: registrationStatusLabel({ ...r, prozess: 'p4_analyse' }),
    sent_at: r.sent_at,
    confirmed_at: r.confirmed_at,
  };
}

/** The proofs of a Brokermandat row (never the notes, the full address or a reply's text). Pure. */
export function registrationProofs(r: RegistrationViewRegRaw): CarrierRowProofs {
  const confirmed = r.confirmed_at !== null || r.confirmation_document_id !== null || r.confirmation_communication_id !== null;
  return {
    meldung: {
      communication_id: r.communication_id,
      sent_at: r.sent_at,
      recipient_email_masked: maskEmail(r.recipient_email),
      attachment_checksum: r.attachment_checksum,
      document_id: r.document_id,
    },
    zustellnachweis: r.communication_id !== null ? { communication_id: r.communication_id, status: 'sent' } : null,
    erinnerung: r.reminded_at !== null ? { communication_id: r.reminder_communication_id, at: r.reminded_at } : null,
    bestaetigung: confirmed
      ? { communication_id: r.confirmation_communication_id, document_id: r.confirmation_document_id, at: r.confirmed_at, mode: r.confirmation_mode }
      : null,
    replies: (r.replies ?? []).map((x) => ({
      id: x.id,
      outcome: x.outcome,
      confidence: Number(x.confidence),
      routing: x.routing,
      decision: x.decision,
      at: x.created_at,
    })),
  };
}

/** A registration status still waiting for a decision (not bestaetigt, abgelehnt, manuell_geschlossen). */
const OPEN_ROW_STATUSES: readonly MandateInsurerNotificationStatus[] = [
  'sending', 'zurueckgestellt', 'zur_freigabe', 'benachrichtigt', 'erinnert', 'klaerung', 'rueckfrage',
];

/**
 * Does the row need a broker: its Brokermandat status, an unclear send, or an exception on an active
 * policy whose registration is still open (a decided one, or a cancelled policy, needs nothing)? Pure.
 */
export function carrierRowHandlungsbedarf(row: {
  broker: CarrierRowBroker | null;
  policies: ReadonlyArray<{ exception_reason: string | null; policy_status: string; registration_open: boolean }>;
}): boolean {
  if (row.policies.some((p) => p.exception_reason !== null && ACTIVE_POLICY_STATUSES.includes(p.policy_status) && p.registration_open)) return true;
  const b = row.broker;
  if (b === null) return false;
  return HANDLUNGSBEDARF_STATUSES.includes(b.status) || b.reminder_error_code === 'versand_unklar' || b.exception_codes.includes('versand_unklar');
}

/** The query rows as the area's rows (labels, derived policy statuses, proofs), by name. Pure. */
export function buildCarrierRows(raw: readonly RegistrationViewRaw[], nowIso: string): CarrierRow[] {
  const rows = raw.map((r): CarrierRow => {
    const policies = (r.policies ?? []).map((p): CarrierRowPolicy => {
      const status = derivePolicyRegistrationStatus(
        {
          status: p.policy_status,
          registration_exception_reason: p.exception_reason,
          registration_inherited_from_policy_id: p.registration_inherited_from_policy_id,
          registration_id: p.registration_id,
        },
        p.registration_status !== null ? { status: p.registration_status } : null,
      );
      return {
        policy_id: p.policy_id,
        policy_number: p.policy_number,
        line_of_business: p.line_of_business,
        policy_status: p.policy_status,
        registration_status: status,
        registration_label: policyRegistrationLabel(status),
        inherited: p.registration_inherited_from_policy_id !== null,
        inherited_from_policy_number: p.inherited_from_policy_number,
        in_mailed_list: p.in_mailed_list === true,
        exception_reason: p.exception_reason,
        courtage_eligible_from: p.courtage_eligible_from,
        courtage_eligible_to: p.courtage_eligible_to,
      };
    });
    const row: CarrierRow = {
      carrier_key: r.carrier_key,
      carrier_uid: r.carrier_uid,
      carrier_name: r.carrier_name,
      versicherer_id: r.versicherer_id,
      carrier_uid_missing: r.carrier_key === REGISTRATION_NO_CARRIER_UID_KEY,
      active_policy_count: policies.filter((p) => ACTIVE_POLICY_STATUSES.includes(p.policy_status)).length,
      handlungsbedarf: false,
      analyse: r.analyse ? analyseOf(r.analyse) : null,
      broker: r.broker ? brokerOf(r.broker, nowIso) : null,
      policies,
      declared_only: (r.declared_only ?? []).map((d) => ({
        relationship_id: d.relationship_id,
        sparte_text: d.sparte_text,
        policy_number: d.policy_number,
        status: d.status,
        source_label: INSURANCE_RELATIONSHIP_SOURCE_LABEL[d.source as InsuranceRelationshipSource] ?? d.source,
      })),
      proofs: r.broker ? registrationProofs(r.broker) : null,
      aufloesung: r.aufloesung
        ? {
            registration_id: r.aufloesung.id,
            mandate_id: r.aufloesung.mandate_id,
            termination_id: r.aufloesung.termination_id,
            status: r.aufloesung.status,
            status_label: registrationStatusLabel({ ...r.aufloesung, prozess: 'p6_aufloesung' }),
            info_only: r.aufloesung.info_only,
            sent_at: r.aufloesung.sent_at,
            response_due_at: r.aufloesung.response_due_at,
            deregistered_at: r.aufloesung.deregistered_at,
            effective_date: r.aufloesung.effective_date,
            escalation_task_id: r.aufloesung.escalation_task_id,
            withdrawal_sent_at: r.aufloesung.withdrawal_sent_at ?? null,
            withdrawal_pending: r.aufloesung.withdrawal_pending ?? false,
          }
        : null,
    };
    // the group without insurer UID is an open item of the close: it always needs a broker
    row.handlungsbedarf = row.carrier_uid_missing || carrierRowHandlungsbedarf({
      broker: row.broker,
      policies: row.policies.map((pol, i) => {
        const regStatus = (r.policies ?? [])[i]?.registration_status ?? null;
        return { ...pol, registration_open: regStatus !== null && OPEN_ROW_STATUSES.includes(regStatus) };
      }),
    });
    return row;
  });
  return sortCarrierRows(rows, 'carrier');
}

/** The area's rows by name (default) or by the Brokermandat status, then by name. Pure. */
export function sortCarrierRows(rows: readonly CarrierRow[], sort: RegistrationViewSort = 'carrier'): CarrierRow[] {
  const name = (r: CarrierRow): string => (r.carrier_name ?? r.carrier_uid ?? r.carrier_key).toLocaleLowerCase('de-CH');
  // the group «Ohne Versicherer-UID» always last
  const last = (r: CarrierRow): number => (r.carrier_uid_missing ? 1 : 0);
  const byName = (a: CarrierRow, b: CarrierRow): number =>
    last(a) - last(b) || name(a).localeCompare(name(b), 'de-CH') || a.carrier_key.localeCompare(b.carrier_key);
  if (sort === 'status') {
    const word = (r: CarrierRow): string => r.broker?.status_label ?? '';
    return [...rows].sort((a, b) => last(a) - last(b) || word(a).localeCompare(word(b), 'de-CH') || byName(a, b));
  }
  return [...rows].sort(byName);
}

/**
 * The filters of the area: «Handlungsbedarf», a status (of the Brokermandat row, or of the
 * Analysemandat row with mandate_kind analyse), and a mandate kind (rows that have that mandate's row).
 * Pure.
 */
export function filterCarrierRows(rows: readonly CarrierRow[], filters: RegistrationViewFilters): CarrierRow[] {
  const statuses = filters.status ? REGISTRATION_VIEW_STATUS_FILTER[filters.status] ?? [] : null;
  const filtered = rows.filter((r) => {
    if (filters.handlungsbedarf === true && !r.handlungsbedarf) return false;
    if (filters.mandate_kind === 'analyse' && r.analyse === null) return false;
    if (filters.mandate_kind === 'broker' && r.broker === null) return false;
    if (statuses !== null) {
      const side = filters.mandate_kind === 'analyse' ? r.analyse : r.broker;
      if (side === null || !statuses.includes(side.status)) return false;
    }
    return true;
  });
  return sortCarrierRows(filtered, filters.sort ?? 'carrier');
}
